
import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"
import { connect } from "react-redux"
import * as Yup from "yup"
import { useFormik } from "formik"

//Import Action
import {
    setBreadcrumbItems,
    getPermissionsList,
    addNewPermission,
    updatePermission,
    deletePermission,
} from "../../../store/actions"

const PermissionsList = props => {
    document.title = "Gestion des Permissions | CRM Admin"

    const [modal, setModal] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [currentPermission, setCurrentPermission] = useState(null)

    const breadcrumbItems = [
        { title: "CRM", link: "#" },
        { title: "Admin", link: "#" },
        { title: "Gestion des Permissions", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Permissions", breadcrumbItems)
        props.getPermissionsList()
    }, [])

    const toggle = () => {
        if (modal) {
            setModal(false)
            setCurrentPermission(null)
            setIsEdit(false)
        } else {
            setModal(true)
        }
    }

    const handlePermissionClick = arg => {
        const permission = arg
        setCurrentPermission({
            id: permission.id,
            name: permission.name,
            module: permission.module,
            type: permission.type,
            description: permission.description || "",
        })
        setIsEdit(true)
        toggle()
    }

    const handleDeletePermission = permission => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette permission ?")) {
            props.deletePermission(permission.id)
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: (currentPermission && currentPermission.name) || "",
            module: (currentPermission && currentPermission.module) || "",
            type: (currentPermission && currentPermission.type) || "system",
            description: (currentPermission && currentPermission.description) || "",
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Veuillez saisir le nom de la permission"),
            module: Yup.string().required("Module requis"),
            type: Yup.string().required("Type requis"),
        }),
        onSubmit: values => {
            if (isEdit) {
                const updatedPermission = {
                    id: currentPermission.id,
                    ...values,
                }
                props.updatePermission(updatedPermission)
            } else {
                props.addNewPermission(values)
            }
            toggle()
        },
    })

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <CardTitle className="h4 mb-0">Gestion des Permissions</CardTitle>
                                <Button color="primary" onClick={toggle}>
                                    <i className="mdi mdi-plus me-1"></i> Créer une Permission
                                </Button>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-centered table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nom</th>
                                            <th>Module</th>
                                            <th>Slug</th>
                                            <th>Type</th>
                                            <th>Description</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.permissions.map((permission, key) => (
                                            <tr key={key}>
                                                <td>{permission.name}</td>
                                                <td>
                                                    <Badge color="light" className="text-primary border font-size-11 px-2 py-1">
                                                        {permission.module}
                                                    </Badge>
                                                </td>
                                                <td><code>{permission.slug}</code></td>
                                                <td>
                                                    <Badge color={permission.type === 'system' ? 'info' : 'secondary'}>
                                                        {permission.type}
                                                    </Badge>
                                                </td>
                                                <td className="text-muted">{permission.description}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => handlePermissionClick(permission)}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleDeletePermission(permission)}
                                                        >
                                                            <i className="mdi mdi-trash-can"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle} tag="h4">
                    {!!isEdit ? "Modifier la Permission" : "Ajouter une Permission"}
                </ModalHeader>
                <ModalBody>
                    <Form
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <div className="mb-3">
                            <Label>Nom de la Permission</Label>
                            <Input
                                name="name"
                                type="text"
                                placeholder="ex: Voir Dashboard"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name || ""}
                                invalid={validation.touched.name && validation.errors.name ? true : false}
                            />
                            {validation.touched.name && validation.errors.name ? (
                                <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                            ) : null}
                        </div>

                        <div className="mb-3">
                            <Label>Module</Label>
                            <Input
                                name="module"
                                type="text"
                                placeholder="ex: Dashboard, Entreprises, Support"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.module || ""}
                                invalid={validation.touched.module && validation.errors.module ? true : false}
                            />
                            {validation.touched.module && validation.errors.module ? (
                                <FormFeedback type="invalid">{validation.errors.module}</FormFeedback>
                            ) : null}
                        </div>

                        <div className="mb-3">
                            <Label>Type</Label>
                            <Input
                                name="type"
                                type="select"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.type || ""}
                            >
                                <option value="system">Système (Super Admin/Admin)</option>
                                <option value="company">Entreprise (Clients)</option>
                            </Input>
                        </div>

                        <div className="mb-3">
                            <Label>Description</Label>
                            <Input
                                name="description"
                                type="textarea"
                                rows="3"
                                placeholder="Description de l'usage..."
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.description || ""}
                            />
                        </div>

                        <div className="text-end">
                            <Button type="button" color="secondary" className="me-2" onClick={toggle}>Annuler</Button>
                            <Button type="submit" color="success">
                                {isEdit ? "Mettre à jour" : "Créer la Permission"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    permissions: state.Permissions.permissions,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getPermissionsList,
    addNewPermission,
    updatePermission,
    deletePermission,
})(PermissionsList)
